import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type WaveBlockContextMenuItem = {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
};

type Props = {
  items: WaveBlockContextMenuItem[];
  ariaLabel?: string;
};

type MenuPosition = {
  top: number;
  left: number;
};

function computeMenuPosition(
  anchor: DOMRect,
  menuWidth: number,
  menuHeight: number,
): MenuPosition {
  const gap = 4;
  const margin = 8;
  let top = anchor.bottom + gap;
  let left = anchor.right - menuWidth;

  if (top + menuHeight > window.innerHeight - margin) {
    top = Math.max(margin, anchor.top - gap - menuHeight);
  }
  if (left < margin) left = margin;
  if (left + menuWidth > window.innerWidth - margin) {
    left = Math.max(margin, window.innerWidth - menuWidth - margin);
  }

  return { top, left };
}

export function WaveBlockContextMenu({
  items,
  ariaLabel = "Azioni widget",
}: Props) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const repositionMenu = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const anchor = btn.getBoundingClientRect();
    const { top, left } = computeMenuPosition(
      anchor,
      menu.offsetWidth,
      menu.offsetHeight,
    );
    setMenuPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    repositionMenu();
    window.addEventListener("scroll", repositionMenu, true);
    window.addEventListener("resize", repositionMenu);
    return () => {
      window.removeEventListener("scroll", repositionMenu, true);
      window.removeEventListener("resize", repositionMenu);
    };
  }, [open, items.length, repositionMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  const run = (item: WaveBlockContextMenuItem) => {
    setOpen(false);
    item.onClick();
  };

  const menuPortal =
    open &&
    createPortal(
      <div
        ref={menuRef}
        className="wave-block-context-menu wave-block-context-menu--portaled"
        role="menu"
        style={
          menuPos
            ? { top: menuPos.top, left: menuPos.left }
            : { visibility: "hidden" as const }
        }
      >
        {items.map((item) => (
          <div key={item.id}>
            {item.separatorBefore && <hr className="wave-block-context-menu-sep" />}
            <button
              type="button"
              role="menuitem"
              className={item.destructive ? "is-destructive" : undefined}
              disabled={item.disabled}
              onClick={() => run(item)}
            >
              {item.label}
            </button>
          </div>
        ))}
      </div>,
      document.body,
    );

  return (
    <div
      className={`wave-block-context-menu-wrap${open ? " is-open" : ""}`}
    >
      <button
        ref={btnRef}
        type="button"
        className="wave-block-context-menu-btn"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {menuPortal}
    </div>
  );
}
